import React, { useState, useEffect } from "react";
import { Table, Form, message } from "antd";
import TagsInput from "react-tagsinput";
import "react-tagsinput/react-tagsinput.css";
import PlusIcon from "../../../assets/Icons/plusIcon";
import DeleteIcon from "../../../assets/Icons/deleteIcon";
import { getColumns } from "./column";
import EditableCell from "./EditableCell";
import {
  FirstRow,
  IconWrapper,
  MainContainer,
  OptionForm,
  OptionNames,
  OptionValues,
  OrangeButton,
  StyledHeading,
  StyledInput,
  StyledLabel,
  VariantsWrapper,
} from "./styles";
import uploadFileToS3 from "../../../utils/s3Upload";

const ProductOptions = ({ payload, setPayload }) => {
  const [options, setOptions] = useState([
    { id: Date.now(), name: "", values: [] },
  ]);
  const [variants, setVariants] = useState([]);

  // Hydrate local option/variant state when switching products (not on every payload edit)
  useEffect(() => {
    if (Array.isArray(payload.options) && payload.options.length > 0) {
      setOptions(
        payload.options.map((opt) => ({
          ...opt,
          id: opt.id || `local-${Date.now()}-${Math.random()}`,
        }))
      );
    } else if (!payload.id) {
      setOptions([{ id: Date.now(), name: "", values: [] }]);
    }

    if (Array.isArray(payload.variants) && payload.variants.length > 0) {
      setVariants(
        payload.variants.map((variant, idx) => ({
          ...variant,
          key: variant.id || idx,
          status: variant.totalStock > 0 ? "IN STOCK" : "OUT OF STOCK",
        }))
      );
    } else {
      setVariants([]);
    }
  }, [payload.id]);
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");

  const handleAddOption = () => {
    const newOption = { id: Date.now(), name: "", values: [] };
    const newOptions = [...options, newOption];
    setOptions(newOptions);
   setPayload((prevPayload) => ({
      ...prevPayload,
      options: newOptions,
    }));
  };

  const handleDeleteOption = (id) => {
    const updatedOptions = options.filter((option) => option.id !== id);
    setOptions(updatedOptions);
    setPayload((prevPayload) => ({
      ...prevPayload,
      options: updatedOptions,
    }));
  };

  const handleOptionNameChange = (id, newName) => {
    const updatedOptions = options.map((option) =>
      option.id === id ? { ...option, name: newName } : option
    );
    setOptions(updatedOptions);
    setPayload((prevPayload) => ({
      ...prevPayload,
      options: updatedOptions,
    }));
  };

  const handleTagChange = (id, newTags) => {
    const updatedOptions = options.map((option) =>
      option.id === id ? { ...option, values: newTags } : option
    );
    setOptions(updatedOptions);
    setPayload((prevPayload) => ({
      ...prevPayload,
      options: updatedOptions,
    }));
  };

  const cartesianProduct = (arrays) => {
    return arrays.reduce(
      (a, b) => a.flatMap((d) => b.map((e) => [d, e].flat())),
      [[]]
    );
  };

  const resolveVariantPrice = () => {
    const productPrice = payload.price;
    if (productPrice === "" || productPrice == null) return 10;
    const parsed = Number(productPrice);
    return Number.isFinite(parsed) ? parsed : 10;
  };

  const generateVariants = () => {
    console.log("Generating variants with options:", options);
    if (options.length === 0) return [];
    const variantPrice = resolveVariantPrice();
    const tagsArrays = options.map((option) => option.values);
    const newVariants = cartesianProduct(tagsArrays).map((variant, index) => ({
      key: index,
      name: variant.join(" / "),
      sku: "",
      MSRP: 0,
      price: variantPrice,
      totalStock: 10,
      status: "IN STOCK",
      image: "",
      imageKey: `variant_image_${index}`,
      uploading: false,
    }));
    // If no combinations produced (e.g., option values are empty), create a default single variant
    let finalVariants = newVariants;
    if (newVariants.length === 0) {
      const defaultName = options.map(opt => opt.name || "").filter(Boolean).join(" / ") || "Default";
      finalVariants = [
        {
          key: Date.now(),
          name: defaultName,
          sku: "",
          MSRP: 0,
          price: 0,
          totalStock: 0,
          status: "OUT OF STOCK",
          image: "",
          imageKey: `variant_image_0`,
          uploading: false,
        },
      ];
      message.info("No option values provided — created a default variant.");
    }
    setVariants(finalVariants);
    setPayload((prevPayload) => ({
      ...prevPayload,
      variants: finalVariants,
    }));
  };

  const handleEdit = (record) => {
    form.setFieldsValue({
      price: record.price,
      totalStock: record.totalStock,
      ...record,
    });
    setEditingKey(String(record.key));
  };

  const handleSave = async (key) => {
    console.log("Saving variant with key:", key);
    try {
      const row = await form.validateFields();
      const newVariants = variants.map((item) => {
        if (String(item.key) === String(key)) {
          return {
            ...item,
            ...row,
            status: row.totalStock > 0 ? "In Stock" : "Out of Stock",
          };
        }
        return item;
      });
      setVariants(newVariants);
      setPayload((prevPayload) => ({
        ...prevPayload,
        variants: newVariants,
      }));
      setEditingKey("");
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const handleCancel = () => {
    setEditingKey("");
  };

  const handleDelete = (key) => {
    const newVariants = variants.filter((item) => String(item.key) !== String(key));
    setVariants(newVariants);
    setPayload((prevPayload) => ({
      ...prevPayload,
      variants: newVariants,
    }));
  };

  const isEditing = (record) => String(record.key) === String(editingKey);


  const handleImageChange = async (info, key) => {
    console.log("info object:", info);
    const fileObj = info.file;
    if (!fileObj) {
      console.error("No file object found");
      return;
    }
    console.log("File to be uploaded:", fileObj);
    const variantIndex = variants.findIndex((variant) => String(variant.key) === String(key));
    if (variantIndex === -1) {
      console.error("Variant not found for key:", key);
      return;
    }
    let updatedVariants = [...variants];
    // Ensure the variant has an imageKey so the form submission can attach files by key
    const filePreviewUrl = URL.createObjectURL(fileObj);
    updatedVariants[variantIndex].uploading = true;
    // set immediate preview so user sees selected image instantly
    updatedVariants[variantIndex].previewUrl = filePreviewUrl;
    if (!updatedVariants[variantIndex].imageKey) {
      updatedVariants[variantIndex].imageKey = `variant_image_${variantIndex}`;
    }
    setVariants(updatedVariants);
    setPayload((prevPayload) => ({ ...prevPayload, variants: updatedVariants }));
    try {
      const { url, key: s3Key } = await uploadFileToS3(fileObj);
      // After successful upload we store the s3Key and url
      // revoke the local preview URL created earlier
      if (updatedVariants[variantIndex].previewUrl) {
        try { URL.revokeObjectURL(updatedVariants[variantIndex].previewUrl); } catch (e) {}
        updatedVariants[variantIndex].previewUrl = undefined;
      }
      updatedVariants[variantIndex].image = url;
      updatedVariants[variantIndex].s3Key = s3Key;
      updatedVariants[variantIndex].uploading = false;
      setVariants(updatedVariants);
      setPayload((prevPayload) => ({ ...prevPayload, variants: updatedVariants }));
      message.success("Variant image uploaded successfully");
    } catch (error) {
      updatedVariants[variantIndex].uploading = false;
      setVariants(updatedVariants);
      setPayload((prevPayload) => ({ ...prevPayload, variants: updatedVariants }));
      console.error("Error uploading variant image:", error);
      message.error("Error uploading variant image");
   }
  };

  const columns = getColumns(handleDelete, handleImageChange, handleEdit, handleSave, handleCancel, editingKey).map((col) => ({
    ...col,
    onCell: (record) => ({
      record,
      editable: col.editable,
      dataIndex: col.dataIndex,
      title: col.title,
      handleSave: () => handleSave(record.key),
      form,
      editing: isEditing(record),
    }),
  }));

  return (
    <MainContainer>
      <FirstRow>
        <StyledHeading>Add Product Variant</StyledHeading>
        <span onClick={() => handleAddOption()}>
          <PlusIcon />
        </span>
      </FirstRow>
      {options.map((option) => (
        <OptionForm key={option.id}>
          <OptionNames>
            <StyledLabel>{`Option`}</StyledLabel>
            <StyledInput
              type="text"
              placeholder="Option title"
              value={option.name}
              onChange={(e) =>
                handleOptionNameChange(option.id, e.target.value)
              }
              required
            />
          </OptionNames>
          <OptionValues>
            <StyledLabel>{`Values`}</StyledLabel>
            <TagsInput
              value={option.values}
              onChange={(values) => {
                console.log("TagsInput onChange triggered with values:", values);
                handleTagChange(option.id, values);
              }}
              inputProps={{ placeholder: "Values" }}
            />
          </OptionValues>
          <IconWrapper onClick={() => handleDeleteOption(option.id)}>
            <DeleteIcon />
          </IconWrapper>
        </OptionForm>
      ))}
      <VariantsWrapper>
        <OrangeButton onClick={generateVariants}>
          Generate Variants
        </OrangeButton>
        {variants.length > 0 ? (
          <div className="variants-list">
            <br />
            <h2>Variants</h2>
            <Form form={form} component={false}>
              <Table
                components={{
                  body: {
                    cell: EditableCell,
                  },
                }}
                bordered={false}
                dataSource={variants}
                columns={columns}
                rowClassName="editable-row"
                pagination={false}
                tableLayout="fixed"
                scroll={{ x: 770 }}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              />
            </Form>
          </div>
        ) : (
          <div className="variants-empty">
            <div className="variants-empty-row">
              <div style={{ width: 120 }}>Image</div>
              <div style={{ flex: 1 }}>Groups</div>
              <div style={{ width: 80, textAlign: 'right' }}>$0.00</div>
              <div style={{ width: 60, textAlign: 'center' }}>0</div>
              <div style={{ width: 140 }}>OUT OF STOCK</div>
            </div>
          </div>
        )}
      </VariantsWrapper>
    </MainContainer>
  );
};

export default ProductOptions;

